import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Target, Users, Shield, Heart, Award, TrendingUp, CheckCircle2, Globe, Zap, Link as LinkIcon } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_release-nexus/artifacts/a0bdi5ug_Alignk%20backlink%20professional.png';

const AboutUs = () => {
  const navigate = useNavigate();

  const handleViewPricing = () => {
    navigate('/');
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const values = [
    {
      icon: <Shield className="h-8 w-8" style={{ color: '#4AC5DB' }} />,
      title: 'Transparency',
      description: 'Complete honesty with our clients. You\'ll always see exactly where your backlinks are placed, with full reporting including live URLs and DA scores.'
    },
    {
      icon: <Award className="h-8 w-8" style={{ color: '#4AC5DB' }} />,
      title: 'Quality Over Quantity',
      description: 'We maintain strict quality standards. Every website in our network is carefully vetted for legitimacy, traffic, and domain authority.'
    },
    {
      icon: <Heart className="h-8 w-8" style={{ color: '#4AC5DB' }} />,
      title: 'Client Success',
      description: 'Your SEO success is our success. We\'re not just building backlinks – we\'re helping you improve your search rankings and grow your business.'
    },
    {
      icon: <Users className="h-8 w-8" style={{ color: '#4AC5DB' }} />,
      title: 'Dedicated Support',
      description: 'Our team is here for you. From choosing the right package to tracking your results, we provide personalized assistance.'
    }
  ];

  const stats = [
    { number: '50,000+', label: 'Backlinks Built' },
    { number: '500+', label: 'Happy Clients' },
    { number: '98%', label: 'Client Satisfaction' },
    { number: '5+', label: 'Years Experience' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img src={LOGO_URL} alt="Alignk" className="h-10" />
            </Link>
            <Link to="/">
              <Button variant="outline" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>
            About <span style={{ color: '#4AC5DB' }}>Alignk</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Professional backlink building services for businesses. We help companies improve their search rankings with high-quality, white-hat link building.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Target className="h-8 w-8" style={{ color: '#4AC5DB' }} />
            <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>Our Story</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-600">
            <p className="mb-4">
              Alignk was founded with a simple mission: help businesses improve their search engine rankings through quality backlink building. Based in London, UK, we've grown from a small team to a leading provider of white-hat SEO link building services.
            </p>
            <p className="mb-4">
              We noticed that many businesses struggled to find reliable backlink providers. The industry was full of spam, PBNs, and low-quality links that could actually harm your SEO. We set out to change that.
            </p>
            <p>
              Today, we work with businesses of all sizes – from startups to established companies – helping them build authority through quality backlinks. Our approach is simple: quality over quantity, transparency in everything we do, and a focus on links that actually help your rankings.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gradient-to-r from-cyan-500 to-teal-500">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.number}</div>
                <div className="text-cyan-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Our <span style={{ color: '#4AC5DB' }}>Values</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do at Alignk.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all">
                <div className="mb-4">{value.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Zap className="h-8 w-8" style={{ color: '#4AC5DB' }} />
            <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>What Sets Us Apart</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold">White-Hat Only</h3>
                <p className="text-gray-600">No PBNs, no spam, no tricks. Just quality editorial backlinks from real websites.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold">Full Transparency</h3>
                <p className="text-gray-600">See every backlink with live URLs, DA scores, and placement details in your dashboard.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold">Quality Network</h3>
                <p className="text-gray-600">500+ vetted websites with high domain authority across multiple industries.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold">Permanent Links</h3>
                <p className="text-gray-600">All backlinks are permanent and do-follow – no temporary or nofollow links.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Globe className="h-8 w-8" style={{ color: '#4AC5DB' }} />
            <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>Contact Information</h2>
          </div>
          <div className="bg-gray-50 rounded-xl p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold mb-4">Business Details</h3>
                <p className="text-gray-600 mb-2"><strong>Company:</strong> Alignk</p>
                <p className="text-gray-600 mb-2"><strong>Location:</strong> London, United Kingdom</p>
                <p className="text-gray-600 mb-2"><strong>Address:</strong> 71-75 Shelton Street, Covent Garden, London WC2H 9JQ</p>
              </div>
              <div>
                <h3 className="font-semibold mb-4">Get In Touch</h3>
                <p className="text-gray-600 mb-2">
                  <strong>Email:</strong>{' '}
                  <a href="mailto:support@alignk.com" className="text-cyan-600 hover:underline">support@alignk.com</a>
                </p>
                <p className="text-gray-600 mb-2">
                  <strong>Phone:</strong>{' '}
                  <a href="tel:+442032397095" className="text-cyan-600 hover:underline">+44 (0) 20323 97095</a>
                </p>
                <p className="text-gray-600">
                  <strong>LinkedIn:</strong>{' '}
                  <a href="https://www.linkedin.com/company/alignkx/" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">linkedin.com/company/alignkx</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-teal-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display' }}>
            Ready to Boost Your SEO?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Start building quality backlinks today and improve your search rankings.
          </p>
          <Button
            onClick={handleViewPricing}
            className="bg-white text-cyan-600 hover:bg-gray-100 text-lg px-8 py-6"
          >
            View Backlink Packages
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} Alignk. All rights reserved.
          </p>
          <div className="mt-4 space-x-4 text-sm">
            <Link to="/privacy-policy" className="text-gray-400 hover:text-white">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-gray-400 hover:text-white">Terms of Use</Link>
            <Link to="/refund-policy" className="text-gray-400 hover:text-white">Refund Policy</Link>
            <Link to="/contact" className="text-gray-400 hover:text-white">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;
